<template>
  <div class="element-type-selector" ref="selectorContainer">
    <button 
      class="selector-trigger"
      @click="toggleDropdown"
    >
      <div class="selector-content">
        <div class="current-element">
          <div v-if="currentElementIcon" class="element-icon" v-html="currentElementIcon"></div>
          <span :class="{'element-label': true, 'placeholder': !currentElement}">{{currentElement ? currentElement.label : placeholder}}</span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M5.5575 6.22125L9 9.65625L12.4425 6.22125L13.5 7.27875L9 11.7788L4.5 7.27875L5.5575 6.22125Z" fill="#374957"/>
        </svg>
      </div>
      
    </button>
    
    <!-- Dropdown List -->
    <div v-if="isDropdownOpen" class="dropdown-list">
      <div class="scroll-area">
        <template v-for="(category, index) in categories" :key="category.name">
          <!-- Category Divider -->
          <div class="category-divider">
            <div class="divider-line"></div>
            <span class="category-label">{{ category.label }}</span>
            <div class="divider-line"></div>
          </div>
          
          <!-- Category Options -->
          <button 
            v-for="elem in category.elements" 
            :key="elem.type"
            class="dropdown-option"
            @click="selectElement(elem)"
          >
            <div v-if="elem.iconSvg" class="element-icon" v-html="elem.iconSvg"></div>
            <span class="element-label">{{ elem.label }}</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SingleSelectorWithSectionalDropdown',
  props: {
    currentElementType: {
      type: String,
      default: ''
    },
    categories: {
      type: Array,
      required: true,
      // Expected format:
      // [
      //   {
      //     name: 'layout',
      //     label: 'Basic Elements',
      //     elements: [{ type: 'section-header', label: 'Section Header', iconSvg: '...' }, ...]
      //   },
      //   ...
      // ]
    },
    placeholder: {
      type: String,
      default: 'Select Element'
    }
  },
  emits: ['element-selected', 'dropdown-visible', 'update:current-element-type'],
  data() {
    return {
      isDropdownOpen: false
    };
  },
  watch:{
    isDropdownOpen(newVal) {
      this.$emit('dropdown-visible', newVal);
    }
  },
  computed: {
    currentElement() {
      for (const category of this.categories) {
        const found = category.elements.find(el => el.type === this.currentElementType);
        if (found) return found;
      }
      return null;
    },
    currentElementLabel() {
      return this.currentElement?.label || 'Select Element';
    },
    currentElementIcon() {
      return this.currentElement?.iconSvg || '';
    }
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    selectElement(element) {
      this.isDropdownOpen = false;
      this.$emit('update:current-element-type', element.type);
      this.$emit('element-selected', element);
    },
    handleClickOutside(e) {
      if (this.$refs.selectorContainer && !this.$refs.selectorContainer.contains(e.target)) {
        this.isDropdownOpen = false;
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.element-type-selector {
  position: relative;
}

.selector-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 200px;
  height: 26px;
  padding: 4px 8px;
  background: white;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  position: relative;
  z-index: 1001;
}
.selector-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
}

.selector-trigger:hover .selector-content {
  background: #f2f2f2;
  border-radius: 4px;
}

.current-element {
  display: flex;
  align-items: center;
  gap: 4px;
  height: fit-content;
  flex: 1;
  overflow: hidden;
}

.element-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.element-icon :deep(svg) {
  max-width: 16px;
  max-height: 16px;
}

.element-label {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.element-label.placeholder {
  color: #c4c4c4;
}

.dropdown-arrow {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.dropdown-list {
  position: absolute;
  top: calc(100% - 0.5px);
  left: 0;
  width: 200px;
  background: white;
  border: 1px solid #C4C4C4;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.scroll-area {
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px;
}

.category-divider {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0 2px 0;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: #E0E0E0;
}

.category-label {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #999;
  text-align: center;
  white-space: nowrap;
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 2px 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  border-radius: 4px;
  transition: background 0.2s;
}

.dropdown-option:hover {
  background: #F2F2F2;
}
</style>
